import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlayCircle, Loader2 } from 'lucide-react';
import { useActiveTours } from '@/hooks/useActiveTours';
import { useTourRestart } from '@/hooks/useTourRestart';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface Tour {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  target_page: string | null;
  created_at: string;
  updated_at: string;
}

export const ActiveToursList: React.FC = () => {
  const { data: tours, isLoading } = useActiveTours();
  const { restartDashboardTour, isRestarting } = useTourRestart();
  const navigate = useNavigate();

  const handleTourStart = async (tour: Tour) => {
    if (tour.name.toLowerCase().includes('dashboard')) {
      // Use existing dashboard tour functionality
      await restartDashboardTour();
    } else if (tour.target_page) {
      // Navigate to target page and show toast for now
      navigate(tour.target_page);
      toast.success(`Starting ${tour.name} tour...`);
    } else {
      toast.info(`${tour.name} tour will be available soon`);
    }
  };

  const getPageDisplayName = (targetPage: string | null) => {
    if (!targetPage) return 'General';
    
    const pageMap: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/dashboard/map': 'Map View',
      '/dashboard/crm/contacts': 'CRM Contacts',
      '/dashboard/crm/deals': 'CRM Deals',
      '/dashboard/partner-portal': 'Partner Portal',
      '/dashboard/equipment': 'Equipment',
      '/features': 'Features',
      '/pricing': 'Pricing'
    };
    
    return pageMap[targetPage] || targetPage;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading tours...</span>
      </div>
    );
  }

  if (!tours || tours.length === 0) {
    return (
      <Card className="text-center py-8">
        <CardContent>
          <p className="text-muted-foreground">No active tours available at the moment.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {tours.map((tour) => (
        <div key={tour.id} className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{tour.name}</h3>
            <Badge variant="outline">{getPageDisplayName(tour.target_page)}</Badge>
          </div>
          
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-muted/20">
              <div className="flex justify-between items-center">
                <div className="space-y-1 flex-1">
                  {tour.description && (
                    <p className="text-sm text-muted-foreground text-left">{tour.description}</p>
                  )}
                </div>
                <Button
                  onClick={() => handleTourStart(tour)}
                  disabled={isRestarting}
                  className="flex items-center gap-2 ml-4 text-black hover:opacity-80"
                  style={{ backgroundColor: "#DFEF97" }}
                >
                  {isRestarting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PlayCircle className="h-4 w-4" />
                  )}
                  Take Tour
                </Button>
              </div>
            </CardHeader>
          </Card>
        </div>
      ))}
    </div>
  );
};